import type { ImageXScopedData } from '@wix/thunderbolt-components-native'
import type { SingleLayoutData } from '@wix/document-services-types'
import type { AnimationDef, ViewMode } from '@wix/thunderbolt-becky-types'

const scrubAnimationMap: { [key: string]: string } = {
	parallax: 'ImageParallax',
	fixed: 'ImageReveal',
}

const getScopedDataForBp = (
	breakpointsToScopedData: Record<string, Array<ImageXScopedData & { breakpoint: string }>>,
	breakpoints: Array<string>
) => {
	const breakpointWithScopedData: string | undefined = breakpoints.find((bp) => breakpointsToScopedData[bp])
	if (!breakpointWithScopedData && breakpointWithScopedData !== '') {
		return null
	}
	return breakpointsToScopedData[breakpointWithScopedData]
}

const getLayoutForBp = (layout: Record<string, SingleLayoutData>, breakpoints: Array<string>) => {
	const breakpointWithData: string | undefined = breakpoints.find(
		(bp) => Object.keys(layout[bp]?.componentLayout || {}).length !== 0
	)
	if (!breakpointWithData && breakpointWithData !== '') {
		return {}
	}

	return layout[breakpointWithData]?.componentLayout
}

export const getResponsiveImageInfoPerBreakpoint = (
	breakpoints: Array<string>,
	scopedData: any,
	layout: Record<string, SingleLayoutData>
) => {
	const breakpointsToScopedData = Object.fromEntries(
		scopedData.map((entry: ImageXScopedData & { breakpoint: string }) => {
			const { breakpoint, ...breakpointScopedData } = entry
			const entryBreakpoint = breakpoint?.replace('#', '') || ''
			return [entryBreakpoint, breakpointScopedData]
		})
	)
	return breakpoints.map((bp, index) => {
		const reversedBreakpoints = breakpoints.slice(0, index + 1).reverse()
		return {
			bp,
			scopedData: getScopedDataForBp(breakpointsToScopedData, reversedBreakpoints),
			layout: getLayoutForBp(layout, reversedBreakpoints),
		}
	})
}

export const getAllImageXScrubAnimations = (
	activeAnimations: {
		[compId: string]: Array<AnimationDef>
	} = {},
	imageXAnimations: Record<string, Array<AnimationDef>>
) => {
	const allCompAnimations = { ...activeAnimations }
	Object.entries(imageXAnimations).forEach(([compId, animations]) => {
		if (!allCompAnimations[compId]) {
			allCompAnimations[compId] = []
		}
		allCompAnimations[compId].push(...animations)
	})
	return allCompAnimations
}

export const imageXSortedScopedDataWithRangeToBgAnimations = (
	scopedDataSortedWithMinMax: Array<any>,
	compId: string,
	dataItemId: string
) => {
	return scopedDataSortedWithMinMax
		.map((datum: any, index: number) => {
			return { ...datum, index }
		})
		.filter((datum: any) => datum.scrollEffect && datum.scrollEffect !== 'none')
		.map((datum: any) => {
			const boundaries = datum.breakpoint
				? [
					{ min: (datum.breakpointRange.max || Infinity) + 1 },
					{ min: 0, max: (datum.breakpointRange.min || 1) - 1 },
				]
				: []
			return {
				action: 'bgScrub',
				name: scrubAnimationMap[datum.scrollEffect as string],
				duration: 1,
				delay: 0,
				targetId: compId,
				viewMode: '' as ViewMode,
				pageId: '',
				behaviorId: `${dataItemId}-${datum.index}`,
				breakpointsToExclude: scopedDataSortedWithMinMax
					.slice(datum.index + 1)
					.map((item: any) => item.breakpointRange)
					.concat(boundaries),
			} as AnimationDef
		})
}
